import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { commonUtils } from 'utils'

import { ISuccessfulQuote } from 'interfaces/quoteResult.interface'
import { Quote } from 'pages/QuoteResult/Tabs/LifeTimeMortgagesTab/LifeTimeMortgagesTab.interface'

export interface ILifeTimeMortgagesFilterData {
  providers: string[]
  erc: string[]
  initialSumRange: {
    min: number
    max: number
  }
  interestRate: {
    min: number
    max: number
  }
  rateType: string[]
  earlyRepaymentChargesPeriod: number
  downsizingProtection: string
  interestType: string
  maximumLoanPermittedRange: {
    min: number
    max: number
  }
  maximumLtvPercentageRange: {
    min: number
    max: number
  }
  repaymentTermRange: {
    min: number
    max: number
  }
  repaymentMandatoryOptions: string[]
}

const initialFilterData: ILifeTimeMortgagesFilterData = {
  providers: [],
  erc: [],
  initialSumRange: {
    min: 0,
    max: 0,
  },
  interestRate: {
    min: 0,
    max: 0,
  },
  rateType: [],
  earlyRepaymentChargesPeriod: 0,
  downsizingProtection: '',
  interestType: '',
  maximumLoanPermittedRange: {
    min: 0,
    max: 0,
  },
  maximumLtvPercentageRange: {
    min: 0,
    max: 0,
  },
  repaymentTermRange: {
    min: 0,
    max: 0,
  },
  repaymentMandatoryOptions: [],
}

export const getKeys = (quoteData: any, providerData: any) => {
  const keys = []
  
  // Product notes indicator
  if (quoteData?.product_notes?.length > 0) keys.push('N')
  
  // Cashback indicators
  if (quoteData?.cashback_ind?.[0] === 'true') keys.push('CB')
  if (quoteData?.cashback_considered_ind?.[0] === 'true') keys.push('CBD')
  
  // Feature indicators
  if (quoteData?.optional_repayments_ind?.[0] === 'true') keys.push('FOR')
  if (quoteData?.free_valuations_ind?.[0] === 'true') keys.push('FV')
  if (quoteData?.special_deal?.[0] === 'Club') keys.push('Special')
  if (quoteData?.guaranteed_quote_ind?.[0] === 'Club') keys.push('GQ')
  
  // KFI availability from provider data
  if (providerData?.kfi_available?.[0] === 'true') keys.push('KFI')
  
  // Rate type indicators
  if (quoteData?.interest_rates?.[0]?.type?.[0] === 'Fixed') keys.push('(F)')
  if (quoteData?.interest_rates?.[0]?.type?.[0] === 'Variable') keys.push('(V)')
  if (quoteData?.interest_rates?.[0]?.type?.[0] === 'Capped') keys.push('(C)')
  
  // Payment frequency indicators (if payment frequency data exists)
  if (quoteData?.payment_frequency?.[0] === 'Yearly') keys.push('p.y.')
  if (quoteData?.payment_frequency?.[0] === 'Quarterly') keys.push('p.q.')
  if (quoteData?.payment_frequency?.[0] === 'Monthly') keys.push('p.m.')

  return keys
}

const useLifeTimeMortgagesTab = () => {
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(window.location.search)
  const idNumber = Number(urlParams.get('idNumber'))
  const [dataSource, setDataSource] = useState<Quote[]>([])
  const successFullQuote: ISuccessfulQuote[] = JSON.parse(localStorage.getItem('quoteResult') || '{}').successFullQuotes
  const [filteredData, setFilteredData] = useState<Quote[]>([])
  const [open, setOpen] = useState(false)
  const [areFilterApplied, setAreFilterApplied] = useState(false)

  const [filterData, setFilterData] = useState<ILifeTimeMortgagesFilterData>(initialFilterData)

  useEffect(() => {
    const dataSource1 = successFullQuote?.map((item: ISuccessfulQuote) => {
      const keys = getKeys(
        item.provider_success?.[0].quote_results?.[0], 
        item.provider_success?.[0].provider_product_data?.[0]
      )
      const providerName = item.provider_success[0].provider_product_data[0].provider_name[0]
      const avatar = commonUtils.getAvatarFromProviderName(providerName)

      const transactionNumber = item.$['perqresp:service_provider_reference']

      const splittedQuoteResponseIdentifier = item.$['perqresp:quote_response_identifier'].split('_')
      const interestType = splittedQuoteResponseIdentifier[splittedQuoteResponseIdentifier.length - 1]
      let modifiedInterestType = ''
      if (interestType === 'Lifetime') modifiedInterestType = 'Roll up'
      if (interestType === 'LifetimeAndInterest') modifiedInterestType = 'Roll up/Interest'
      if (interestType === 'Interest') modifiedInterestType = 'Interest only'

      return {
        id: item.$['perqresp:quote_response_identifier'],
        provider: providerName,
        product: item.provider_success[0].provider_product_data[0].provider_variant_name[0],
        initialSum: item.provider_success[0].quote_results[0].payments[0].initial_only?.[0].initial_amount[0]._,
        reserve: item.provider_success[0].quote_results[0].payments[0].reserve?.[0].reserve_amount[0]._,
        netLoanAmount: item.provider_success[0].quote_results[0].net_loan_amount[0]._,
        mer: item.provider_success[0].quote_results[0].interest_rates[0].mer[0],
        aer: item.provider_success[0].quote_results[0].interest_rates[0].aer[0],
        costPerPound: item.provider_success[0].quote_results[0].cost_per_pound?.[0]._ || '0',
        totalCostOfLoan: item.provider_success[0].quote_results[0].total_cost_of_loan?.[0]._ || '0',
        rateType: item.provider_success[0].quote_results[0].interest_rates[0].type[0],
        notes: item.provider_success[0].quote_results[0].product_notes?.[0].product_note || [],
        transactionNumber: transactionNumber,
        avatar: avatar,
        keys,
        commission: item.provider_success[0].quote_results[0].commission_paid?.[0]._ || '0',
        earlyRepaymentChargesPeriod: Number(item.provider_success[0].quote_results[0].early_repayment_charge[0].years || 0),
        erc: item.provider_success[0].quote_results[0].early_repayment_charge[0].type[0],
        downsizingProtection: item.provider_success[0].quote_results[0].downsizing_protection_ind[0] === 'true' ? 'Yes' : 'No',
        interestType: modifiedInterestType,
        maximumLoanPermitted: item.provider_success[0].quote_results[0].maximum_loan_permitted?.[0]._,
        maximumLtvPercentage: item.provider_success[0].quote_results[0].maximum_ltv_percentage?.[0],
        repayment_details: item.provider_success[0].quote_results[0].repayment_details,
        productFee: item.provider_success[0].quote_results[0].fees?.[0]?.total?.[0]?._,
      }
    })
    setDataSource(dataSource1)
    setFilteredData(dataSource1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const generateFilterOptions = () => {
    const providerNames = dataSource?.map((item) => item.provider)
    const providerCount = providerNames.reduce((acc, curr) => {
      acc[curr] = (acc[curr] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const providerOptions = Object.entries(providerCount).map(([providerName, count]) => ({
      label: `${providerName} (${count})`,
      value: providerName,
    }))

    const downsizingOptions = dataSource?.map((item) => item.downsizingProtection)
    const downsizingProtectionOptionCount = downsizingOptions.reduce((acc, curr) => {
      acc[curr] = (acc[curr] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const downsizingProtectionOptions = Object.entries(downsizingProtectionOptionCount).map(([downsizingOption, count]) => ({
      label: `${downsizingOption} (${count})`,
      value: downsizingOption,
    }))

    const interestTypes = dataSource?.map((item) => item.interestType)
    const interestTypeCount = interestTypes.reduce((acc, curr) => {
      acc[curr] = (acc[curr] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const interestTypeOptions = Object.entries(interestTypeCount).map(([interestType, count]) => ({
      label: `${interestType} (${count})`,
      value: interestType,
    }))

    const earlyRepaymentCharges = dataSource?.map((item) => item.erc)
    const ercCount = earlyRepaymentCharges.reduce((acc, curr) => {
      acc[curr] = (acc[curr] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const ercOptions = Object.entries(ercCount).map(([ercType, count]) => ({
      label: `${ercType} (${count})`,
      value: ercType,
    }))

    // Get all initialSum and aer values and convert them to numbers
    const initialSums = dataSource?.map((item) => Number(item.initialSum))
    const interestRates = dataSource?.map((item) => Number(item.aer))
    
    // Get all maximumLoanPermitted and maximumLtvPercentage values and convert them to numbers
    const maximumLoanPermittedValues = dataSource?.map((item) => Number(item.maximumLoanPermitted)).filter(val => !isNaN(val))
    const maximumLtvPercentageValues = dataSource?.map((item) => Number(item.maximumLtvPercentage)).filter(val => !isNaN(val))

    // Find the lowest and highest initialSum
    const lowestInitialSum = Math.min(...initialSums)
    const highestInitialSum = Math.max(...initialSums)

    // Find the lowest and highest interestRate
    const lowestInterestRate = Math.min(...interestRates)
    const highestInterestRate = Math.max(...interestRates)
    
    // Find the lowest and highest maximumLoanPermitted
    const lowestMaximumLoanPermitted = maximumLoanPermittedValues.length > 0 ? Math.min(...maximumLoanPermittedValues) : 0
    const highestMaximumLoanPermitted = maximumLoanPermittedValues.length > 0 ? Math.max(...maximumLoanPermittedValues) : 0
    
    // Find the lowest and highest maximumLtvPercentage
    const lowestMaximumLtvPercentage = maximumLtvPercentageValues.length > 0 ? Math.min(...maximumLtvPercentageValues) : 0
    const highestMaximumLtvPercentage = maximumLtvPercentageValues.length > 0 ? Math.max(...maximumLtvPercentageValues) : 0

    const rateTypes = dataSource?.map((item) => item.rateType)

    // Create a unique set of rateTypes
    const uniqueRateTypes = Array.from(new Set(rateTypes))

    // Create rateType options
    const rateTypeOptions = uniqueRateTypes.map((rateType) => ({
      label: rateType,
      value: rateType,
    }))

    // Get all earlyRepaymentChargesPeriod values and convert them to numbers
    const earlyRepaymentChargesPeriods = dataSource?.map((item) => Number(item.earlyRepaymentChargesPeriod))

    // Filter out 0 values
    const filteredEarlyRepaymentChargesPeriods = earlyRepaymentChargesPeriods.filter((item) => item !== 0)

    // Find the lowest and highest earlyRepaymentChargesPeriod
    const lowestEarlyRepaymentChargesPeriod = Math.min(...filteredEarlyRepaymentChargesPeriods)
    const highestEarlyRepaymentChargesPeriod = Math.max(...filteredEarlyRepaymentChargesPeriods)

    // Repayment term range
    const repaymentTermsRaw = dataSource?.map((item) => {
      const termStr = item.repayment_details?.[0]?.repayment_term?.[0];
      if (termStr && !isNaN(Number(termStr))) {
        return Number(termStr);
      }
      return null;
    });
    const repaymentTerms = repaymentTermsRaw.filter((val): val is number => val !== null);
    const lowestRepaymentTerm = repaymentTerms.length > 0 ? Math.min(...repaymentTerms) : undefined;
    const highestRepaymentTerm = repaymentTerms.length > 0 ? Math.max(...repaymentTerms) : undefined;

    // Repayment mandatory options
    const repaymentMandatoryOptions = [
      'Products with no fixed interest servicing term',
      'Interest payments required during payment term',
      'Interest payments can cease during payment term',
    ]

    return {
      providerOptions,
      downsizingProtectionOptions,
      interestTypeOptions,
      ercOptions,
      initialSumRange: {
        min: lowestInitialSum,
        max: highestInitialSum,
      },
      interestRateRange: {
        min: lowestInterestRate,
        max: highestInterestRate,
      },
      rateTypeOptions,
      earlyRepaymentChargesPeriodRange: {
        min: lowestEarlyRepaymentChargesPeriod,
        max: highestEarlyRepaymentChargesPeriod,
      },
      maximumLoanPermittedRange: {
        min: lowestMaximumLoanPermitted,
        max: highestMaximumLoanPermitted,
      },
      maximumLtvPercentageRange: {
        min: lowestMaximumLtvPercentage,
        max: highestMaximumLtvPercentage,
      },
      repaymentTermRange: {
        min: lowestRepaymentTerm ?? '',
        max: highestRepaymentTerm ?? '',
      },
      repaymentMandatoryOptions,
    }
  }

  const onRowClick = useCallback((rowProps: any, event: any) => {
    const quoteDetailId = rowProps.data.id
    navigate(`/quote-details?id=${quoteDetailId}&idNumber=${idNumber}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilter = (filterData: any) => {
    let dataToFilter = dataSource

    if (filterData.providers.length > 0) {
      dataToFilter = dataToFilter.filter((item) => filterData.providers.includes(item.provider))
      setAreFilterApplied(true)
    }

    if (filterData.erc.length > 0) {
      dataToFilter = dataToFilter.filter((item) => filterData.erc.includes(item.erc))
      setAreFilterApplied(true)
    }

    if (filterData.initialSumRange.min > 0) {
      dataToFilter = dataToFilter.filter((item) => item.initialSum !== undefined && item.initialSum >= filterData.initialSumRange.min)
      setAreFilterApplied(true)
    }

    if (filterData.initialSumRange.max > 0) {
      dataToFilter = dataToFilter.filter((item) => item.initialSum !== undefined && item.initialSum <= filterData.initialSumRange.max)
      setAreFilterApplied(true)
    }

    if (filterData.interestRate.min > 0) {
      dataToFilter = dataToFilter.filter((item) => item.aer >= filterData.interestRate.min)
      setAreFilterApplied(true)
    }

    if (filterData.interestRate.max > 0) {
      dataToFilter = dataToFilter.filter((item) => item.aer <= filterData.interestRate.max)
      setAreFilterApplied(true)
    }

    if (filterData.rateType.length > 0) {
      dataToFilter = dataToFilter.filter((item) => filterData.rateType.includes(item.rateType))
      setAreFilterApplied(true)
    }

    if (filterData.earlyRepaymentChargesPeriod > 0) {
      dataToFilter = dataToFilter.filter((item) => item.earlyRepaymentChargesPeriod === filterData.earlyRepaymentChargesPeriod)
      setAreFilterApplied(true)
    }

    if (filterData.downsizingProtection?.length > 0) {
      dataToFilter = dataToFilter.filter((item) => filterData.downsizingProtection.includes(item.downsizingProtection))
      setAreFilterApplied(true)
    }

    if (filterData.interestType?.length > 0) {
      dataToFilter = dataToFilter.filter((item) => filterData.interestType.includes(item.interestType))
      setAreFilterApplied(true)
    }

    if (filterData.maximumLoanPermittedRange.min > 0) {
      dataToFilter = dataToFilter.filter((item) => item.maximumLoanPermitted !== undefined && Number(item.maximumLoanPermitted) >= filterData.maximumLoanPermittedRange.min)
      setAreFilterApplied(true)
    }

    if (filterData.maximumLoanPermittedRange.max > 0) {
      dataToFilter = dataToFilter.filter((item) => item.maximumLoanPermitted !== undefined && Number(item.maximumLoanPermitted) <= filterData.maximumLoanPermittedRange.max)
      setAreFilterApplied(true)
    }

    if (filterData.maximumLtvPercentageRange.min > 0) {
      dataToFilter = dataToFilter.filter((item) => item.maximumLtvPercentage !== undefined && Number(item.maximumLtvPercentage) >= filterData.maximumLtvPercentageRange.min)
      setAreFilterApplied(true)
    }

    if (filterData.maximumLtvPercentageRange.max > 0) {
      dataToFilter = dataToFilter.filter((item) => item.maximumLtvPercentage !== undefined && Number(item.maximumLtvPercentage) <= filterData.maximumLtvPercentageRange.max)
      setAreFilterApplied(true)
    }

    // Repayment term filter (range)
    if (filterData.repaymentTermRange.min > 0) {
      dataToFilter = dataToFilter.filter((item) => {
        const term = item.repayment_details?.[0]?.repayment_term?.[0]
        return term && Number(term) >= filterData.repaymentTermRange.min
      })
      setAreFilterApplied(true)
    }
    if (filterData.repaymentTermRange.max > 0) {
      dataToFilter = dataToFilter.filter((item) => {
        const term = item.repayment_details?.[0]?.repayment_term?.[0]
        return term && Number(term) <= filterData.repaymentTermRange.max
      })
      setAreFilterApplied(true)
    }

    // Repayment mandatory filter (multi-select)
    if (filterData.repaymentMandatoryOptions?.length > 0) {
      dataToFilter = dataToFilter.filter((item) => {
        const term = item.repayment_details?.[0]?.repayment_term?.[0]
        const mandatory = item.repayment_details?.[0]?.repayment_mandatory_ind?.[0]
        // Category 1: No payment term
        if (!term && filterData.repaymentMandatoryOptions.includes('Products with no fixed interest servicing term')) {
          return true
        }
        // Category 2: Payment term exists and mandatory is true
        if (term && mandatory === 'true' && filterData.repaymentMandatoryOptions.includes('Interest payments required during payment term')) {
          return true
        }
        // Category 3: Payment term exists and mandatory is false
        if (term && mandatory === 'false' && filterData.repaymentMandatoryOptions.includes('Interest payments can cease during payment term')) {
          return true
        }
        return false
      })
      setAreFilterApplied(true)
    }

    setFilteredData(dataToFilter)
    setOpen(false)
  }

  const closeDrawer = () => {
    setOpen(false)
  }

  const clearFilters = () => {
    setAreFilterApplied(false)
    setFilterData(initialFilterData)
    handleFilter(initialFilterData)
  }

  return {
    areFilterApplied,
    clearFilters,
    filteredData,
    open,
    setOpen,
    filterData,
    setFilterData,
    onRowClick,
    closeDrawer,
    handleFilter,
    generateFilterOptions,
  }
}

export default useLifeTimeMortgagesTab
